import React, { useMemo, useState } from 'react';
import ReadinessChip from './ReadinessChip.jsx';
import { downloadGapListCsv, downloadGapListXlsx } from './exportGapList.js';
import { downloadRequestListDocx } from './exportRequestList.js';
import { useChecklist } from './useChecklist.js';
import { CATEGORY_LABEL, CHECKLIST_NAME, OWNERS, PHASE_LABEL, checklistForAssistant, readinessHeadline } from './checklist.js';
import { useAssistantSummary } from '../meeting/common.jsx';

const PHASES = ['pre_offer', 'pre_closing', 'post_closing'];
const CATS = Object.keys(CATEGORY_LABEL);
const PILL = { Complete: 'pass', Partial: 'review', Missing: 'fail', 'N/A': 'insufficient', Upcoming: 'neutral' };
const OWNER_SHORT = { Accounting: 'Acct', Operations: 'Ops', Land: 'Land', Legal: 'Legal', Engineering: 'Eng' };

/** Field evidence refs map onto the reference dashboards. */
const REF_NAV = [
  [/^cashflow/, 'cashflow'],
  [/^(wells|masterWells|statusCounts|well-file|capex|wellType)/, 'wells'],
  [/^(acreageUnits|counties)/, 'land'],
  [/^map$/, 'map'],
  [/^lasFlags/, 'geoscience'],
  [/^(provenance|cases)$/, 'reserves'],
  [/^(contract:|vdrStatus|PO-F-04|PO-L-03)/, 'diligence'],
];

function navFor(ref) {
  if (!ref) return null;
  if (ref.startsWith('item:')) return { anchor: ref.slice(5) };
  for (const [re, key] of REF_NAV) if (re.test(ref)) return { key };
  return null;
}

function shortDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function fmtCaptured(field, value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (field.type === 'currency' && Number.isFinite(n)) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (field.type === 'integer' && Number.isFinite(n)) return n.toLocaleString('en-US');
  if (field.type === 'date') return shortDate(String(value));
  return String(value);
}

function isGap(item) {
  return item.overdue || item.status === 'Missing' || item.status === 'Partial';
}

function actionLabel(item) {
  if (item.method === 'manual') return item.kind === 'signoff' ? 'Sign off' : item.kind === 'task' ? 'Mark done' : 'Attest';
  return 'Override';
}

export default function ChecklistPanel({ dealId, deal, pkg, diligence, wells, land, map, geoscience, onNavigate }) {
  const { result, save, saveSettings } = useChecklist({ dealId, pkg, diligence, wells, land, map, geoscience });
  const [openId, setOpenId] = useState('');
  const [draft, setDraft] = useState({ status: 'Complete', note: '', values: {} });
  const [errId, setErrId] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState('gaps');
  const [owner, setOwner] = useState('');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState({});
  const [closingDraft, setClosingDraft] = useState(null);

  useAssistantSummary({
    surface: 'checklist',
    deal: deal?.label || dealId,
    checklist: checklistForAssistant(result),
  });

  const label = deal?.label || dealId;
  const byId = useMemo(() => Object.fromEntries((result?.items || []).map((i) => [i.id, i])), [result]);
  const q = query.trim().toLowerCase();

  if (!result) {
    return (
      <div className="mp" data-testid="diligence-checklist">
        <p className="mp-note">Loading the checklist…</p>
      </div>
    );
  }

  const pre = result.readiness.pre_offer;
  const overdue = result.warnings.overdue;
  const closingValue = closingDraft == null ? result.dates.closingDate || '' : closingDraft;

  function rowsFor(phase) {
    return result.items.filter((i) => {
      if (i.phase !== phase) return false;
      if (owner && i.owner !== owner) return false;
      if (view === 'gaps' && !isGap(i)) return false;
      if (q && !`${i.id} ${i.item} ${i.whatSatisfies} ${i.owner} ${i.detail}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }

  function openForm(item) {
    setErr('');
    setErrId('');
    if (openId === item.id) {
      setOpenId('');
      return;
    }
    setOpenId(item.id);
    setDraft({
      // Attest / Sign off / Mark done default to Complete; a computed row keeps its status.
      status:
        item.override?.status ||
        (item.method === 'manual' || item.status === 'N/A' || item.status === 'Upcoming' ? 'Complete' : item.status),
      note: item.override?.note || '',
      values: { ...(item.override?.values || {}) },
    });
  }

  async function onSave(item) {
    setErr('');
    setBusy(true);
    try {
      await save({
        itemId: item.id,
        status: draft.status,
        note: draft.note,
        values: draft.values,
        catalogVersion: result.catalogVersion,
      });
      setOpenId('');
    } catch (e) {
      setErrId(item.id);
      setErr(e.message || 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  async function onClear(item) {
    setErr('');
    setBusy(true);
    try {
      await save({ itemId: item.id, clear: true });
      setOpenId('');
    } catch (e) {
      setErrId(item.id);
      setErr(e.message || 'Could not clear');
    } finally {
      setBusy(false);
    }
  }

  async function onStage(stage) {
    setErr('');
    setErrId('');
    try {
      await saveSettings({ stage });
    } catch (e) {
      setErrId('__settings__');
      setErr(e.message || 'Could not update the stage');
    }
  }

  async function onClosing() {
    setErr('');
    setErrId('');
    try {
      await saveSettings(closingValue ? { closingDate: closingValue } : { clearClosingDate: true });
      setClosingDraft(null);
    } catch (e) {
      setErrId('__settings__');
      setErr(e.message || 'Could not save the closing date');
    }
  }

  function goTo(nav) {
    if (!nav || !onNavigate) return;
    if (nav.anchor) {
      setView('all');
      setExpanded((e) => ({ ...e, [byId[nav.anchor]?.phase]: true }));
      window.setTimeout(() => document.getElementById(`cl-${nav.anchor}`)?.scrollIntoView({ block: 'center' }), 50);
      return;
    }
    onNavigate(nav.key);
  }

  const jurisdiction = [
    (result.deal.states || []).join(' / ') || null,
    result.deal.operated === false ? 'non-operated' : result.deal.operated ? 'operated' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="mp" data-testid="diligence-checklist">
      <div className="mp-head">
        <div>
          <span className="mp-eyebrow">
            {CHECKLIST_NAME} · {result.catalogVersion.split('.').pop()}
            {jurisdiction ? ` · ${jurisdiction}` : ''}
          </span>
          <h2 className="mp-h2">Checklist</h2>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <ReadinessChip pct={pre.pct} complete={pre.complete} partial={pre.partial} required={pre.required} overdue={overdue} />
          <button
            type="button"
            className="mp-btn small"
            data-testid="checklist-export-request"
            onClick={() =>
              downloadRequestListDocx(result, {
                dealId,
                dealLabel: label,
                seller: pkg?.meta?.seller || deal?.seller || 'Seller',
                buyer: result.deal.buyer?.name,
              })
            }
          >
            Request list (docx)
          </button>
          <button type="button" className="mp-btn ghost small" data-testid="checklist-export-xlsx" onClick={() => downloadGapListXlsx(result, `${dealId}-diligence-gap-list.xlsx`, { dealLabel: label })}>
            Gap list (xlsx)
          </button>
          <button type="button" className="mp-btn ghost small" data-testid="checklist-export-csv" onClick={() => downloadGapListCsv(result, `${dealId}-diligence-gap-list.csv`)}>
            Gap list (CSV)
          </button>
        </div>
      </div>

      <section className="mp-card" aria-label="Readiness" data-testid="checklist-summary">
        <div className="mp-tiles">
          {CATS.map((cat) => {
            const r = result.byCategory.pre_offer[cat];
            return (
              <div key={cat} className="mp-tile" data-testid={`readiness-${cat}`}>
                <div className="k">{CATEGORY_LABEL[cat]}</div>
                <div className="v">{r.pct == null ? '—' : `${r.pct}%`}</div>
                <div className="s">
                  {r.complete} of {r.required} required in hand · {r.missing} missing
                </div>
              </div>
            );
          })}
          <div className={`mp-tile ${overdue ? 'bad' : ''}`} data-testid="readiness-overdue">
            <div className="k">Overdue tasks</div>
            <div className="v">{overdue}</div>
            <div className="s">{result.dates.closingDate ? `Closing ${shortDate(result.dates.closingDate)}` : 'Set a closing date to schedule tasks'}</div>
          </div>
        </div>
        <div className="cl-settings">
          <label className="cl-setting">
            <span>Deal stage</span>
            <select id="checklist-stage" data-testid="checklist-stage" value={result.stage} onChange={(e) => onStage(e.target.value)}>
              {PHASES.map((p) => (
                <option key={p} value={p}>
                  {PHASE_LABEL[p]}
                </option>
              ))}
            </select>
          </label>
          <span className="cl-setting">
            <span>Effective date</span>
            <b className="mp-num">{result.dates.effectiveDate ? shortDate(result.dates.effectiveDate) : '—'}</b>
          </span>
          <label className="cl-setting">
            <span>Closing date</span>
            <input
              type="date"
              className="mp-input"
              data-testid="checklist-closing"
              value={closingValue}
              onChange={(e) => setClosingDraft(e.target.value)}
              onBlur={() => closingDraft != null && closingDraft !== (result.dates.closingDate || '') && onClosing()}
            />
          </label>
          <span className="mp-note">
            Readiness scores required pre-offer items: Complete 1, Partial ½. Pre-closing tasks are due against the closing date, post-closing against the effective date. Warnings only, nothing is gated.
          </span>
        </div>
        {err && errId === '__settings__' ? (
          <div className="mp-note" role="alert" style={{ color: 'var(--mp-red)' }}>
            {err}
          </div>
        ) : null}
      </section>

      <div className="cl-toolbar">
        <div className="mp-seg" role="group" aria-label="Rows">
          <button type="button" aria-pressed={view === 'gaps'} onClick={() => setView('gaps')} data-testid="view-gaps">
            Gaps
          </button>
          <button type="button" aria-pressed={view === 'all'} onClick={() => setView('all')} data-testid="view-all">
            All items
          </button>
        </div>
        <div className="mp-seg" role="group" aria-label="Owner">
          <button type="button" aria-pressed={!owner} onClick={() => setOwner('')}>
            Everyone
          </button>
          {OWNERS.map((o) => (
            <button key={o} type="button" aria-pressed={owner === o} onClick={() => setOwner(owner === o ? '' : o)} data-testid={`owner-${o}`}>
              {o}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="mp-input cl-search"
          placeholder="Search items"
          aria-label="Search items"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {PHASES.map((phase) => {
        const inPhase = result.items.filter((i) => i.phase === phase);
        const reached = inPhase.some((i) => i.reached);
        const ready = result.readiness[phase];
        const rows = rowsFor(phase);
        const isOpen = reached || expanded[phase];
        const dated = inPhase.filter((i) => i.dueDate).map((i) => i.dueDate).sort();
        return (
          <section key={phase} className="mp-card" aria-label={PHASE_LABEL[phase]} data-testid={`phase-${phase}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <h3 className="mp-h3">
                {PHASE_LABEL[phase]}
                {!reached ? <span className="mp-badge neutral" style={{ marginLeft: 10 }}>Upcoming</span> : null}
              </h3>
              <span className="mp-note">
                {reached
                  ? `${readinessHeadline(ready) || '—'}${ready.missing != null ? ` · ${ready.missing} missing` : ''}${ready.overdue ? ` · ${ready.overdue} overdue` : ''}`
                  : `${inPhase.length} tasks${dated.length ? ` · due ${shortDate(dated[0])} to ${shortDate(dated[dated.length - 1])}` : ''}`}
              </span>
            </div>
            {!reached ? (
              <div className="mp-note">
                Opens when the deal stage is set to {PHASE_LABEL[phase]}.{' '}
                {dated.length ? 'Due dates are already scheduled from the closing and effective dates.' : 'Set a closing date to schedule these.'}
                {' '}
                <button type="button" className="mp-link" onClick={() => setExpanded((e) => ({ ...e, [phase]: !e[phase] }))} data-testid={`toggle-${phase}`}>
                  {expanded[phase] ? 'Hide the tasks' : 'Show the tasks'}
                </button>
              </div>
            ) : null}
            {isOpen && !rows.length ? (
              <div className="mp-note">{view === 'gaps' && !q && !owner ? 'No gaps in this phase.' : 'Nothing matches the filter.'}</div>
            ) : null}
            {isOpen
              ? CATS.map((cat) => {
                  const catRows = rows.filter((i) => i.category === cat);
                  if (!catRows.length) return null;
                  return (
                    <div key={cat}>
                      <div className="mp-eyebrow" style={{ marginTop: 12 }}>{CATEGORY_LABEL[cat]}</div>
                      {catRows.map((item) => (
                        <Row
                          key={item.id}
                          item={item}
                          byId={byId}
                          open={openId === item.id}
                          draft={draft}
                          setDraft={setDraft}
                          busy={busy}
                          err={errId === item.id ? err : ''}
                          onOpen={() => openForm(item)}
                          onSave={() => onSave(item)}
                          onClear={() => onClear(item)}
                          onCancel={() => {
                            setOpenId('');
                            setErr('');
                            setErrId('');
                          }}
                          goTo={goTo}
                          onNavigate={onNavigate}
                        />
                      ))}
                    </div>
                  );
                })
              : null}
          </section>
        );
      })}
    </div>
  );
}

function Row({ item, byId, open, draft, setDraft, busy, err, onOpen, onSave, onClear, onCancel, goTo, onNavigate }) {
  const pill = item.overdue ? 'fail' : PILL[item.status] || 'insufficient';
  const captured = (item.fields || [])
    .map((f) => ({ f, text: fmtCaptured(f, item.override?.values?.[f.key]) }))
    .filter((x) => x.text);
  return (
    <div id={`cl-${item.id}`} className="mp-list-item cl-row" data-testid={`checklist-item-${item.id}`} data-status={item.status}>
      <span className={`mp-badge ${pill}`} style={{ minWidth: 76 }}>
        {item.overdue ? 'Overdue' : item.status}
      </span>
      <div className="body">
        <div className="title" style={{ fontSize: 15 }}>
          {item.item}
          {item.requiredForPhase ? <span className="cl-tag">required</span> : null}
        </div>
        <div className="cl-meta">
          <span className="cl-owner" title="Owner">{item.owner}</span>
          {item.dueDate ? (
            <span className={item.overdue ? 'cl-due overdue' : 'cl-due'}>
              Due {shortDate(item.dueDate)}
            </span>
          ) : null}
          <span className="mp-num cl-id">{item.id}</span>
          {item.source === 'override' && item.override ? (
            <span>
              {item.method === 'manual' ? 'Attested' : 'Overridden'} by {item.override.userName}
              {item.override.updatedAt ? ` · ${new Date(item.override.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
            </span>
          ) : null}
        </div>
        <div className="text">{item.whatSatisfies}</div>
        {item.detail ? <div className="mp-note">{item.detail}</div> : null}
        {captured.length ? (
          <div className="cl-chips" data-testid={`captured-${item.id}`}>
            {captured.map(({ f, text }) => (
              <span key={f.key} className="cl-chip value">
                {f.label}: {text}
              </span>
            ))}
          </div>
        ) : null}
        {item.evidence?.length ? (
          <div className="cl-chips">
            {item.evidence.map((ev, i) => {
              if (ev.kind === 'doc' && ev.docId) {
                return (
                  <button
                    key={`${ev.docId}-${i}`}
                    type="button"
                    className="mp-link"
                    data-testid={`evidence-${item.id}`}
                    onClick={() => onNavigate?.(`dataroom?doc=${encodeURIComponent(ev.docId)}&docType=${encodeURIComponent(ev.docType || '')}`)}
                  >
                    {ev.label}
                  </button>
                );
              }
              const nav = navFor(ev.ref);
              return nav ? (
                <button key={`${ev.label}-${i}`} type="button" className="cl-chip link" onClick={() => goTo(nav)}>
                  {ev.label}
                </button>
              ) : (
                <span key={`${ev.label}-${i}`} className="cl-chip">{ev.label}</span>
              );
            })}
          </div>
        ) : null}
        {item.feeds?.length ? (
          <div className="cl-chips">
            <span className="mp-note">Feeds</span>
            {item.feeds.map((id) => (
              <button key={id} type="button" className="cl-chip link" onClick={() => goTo({ anchor: id })} title={byId[id]?.item}>
                {id} · {byId[id]?.item}
              </button>
            ))}
          </div>
        ) : null}
        {open ? (
          <div className="cl-form" data-testid={`override-form-${item.id}`}>
            <label className="cl-field">
              <span>Status</span>
              <select value={draft.status} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}>
                {['Complete', 'Partial', 'Missing', 'N/A'].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            {(item.fields || []).map((f) => (
              <label key={f.key} className="cl-field">
                <span>{f.label}</span>
                {f.type === 'select' ? (
                  <select value={draft.values[f.key] ?? ''} onChange={(e) => setDraft((d) => ({ ...d, values: { ...d.values, [f.key]: e.target.value } }))}>
                    <option value="">—</option>
                    {(f.options || []).map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={f.type === 'date' ? 'date' : f.type === 'text' ? 'text' : 'number'}
                    min={f.type === 'integer' || f.type === 'currency' ? 0 : undefined}
                    step={f.type === 'integer' ? 1 : f.type === 'currency' ? '0.01' : undefined}
                    value={draft.values[f.key] ?? ''}
                    onChange={(e) => setDraft((d) => ({ ...d, values: { ...d.values, [f.key]: e.target.value } }))}
                    data-testid={`field-${item.id}-${f.key}`}
                  />
                )}
              </label>
            ))}
            <label className="cl-field wide">
              <span>Note (required)</span>
              <textarea
                value={draft.note}
                placeholder={item.id === 'PC-F-02' ? 'Attest only. Never enter bank details.' : 'Who confirmed it, where it is, what is still open'}
                onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
                rows={2}
              />
            </label>
            {err ? (
              <div className="mp-note wide" role="alert" style={{ color: 'var(--mp-red)' }}>
                {err}
              </div>
            ) : null}
            <div className="cl-actions wide">
              <button type="button" className="mp-btn amber small" disabled={busy || !draft.note.trim()} onClick={onSave} data-testid={`save-${item.id}`}>
                Save
              </button>
              <button type="button" className="mp-btn ghost small" disabled={busy} onClick={onCancel}>
                Cancel
              </button>
              {item.source === 'override' ? (
                <button type="button" className="mp-btn ghost small" disabled={busy} onClick={onClear}>
                  Use computed
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      <button type="button" className="mp-btn ghost small" data-testid={`override-${item.id}`} aria-expanded={open} onClick={onOpen}>
        {actionLabel(item)}
      </button>
    </div>
  );
}
